import { StrictMode, useSyncExternalStore, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { App } from './app/App';
import { createAuthQueryClientStore } from './app/auth-context';
import { EntityColorPaletteProvider } from './shared/ui/EntityColorPaletteProvider';
import './styles.css';

const authQueries = createAuthQueryClientStore(() => new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      retry: false,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
}));

if (import.meta.hot) import.meta.hot.dispose(authQueries.dispose);

function AuthQueryProvider({ children }: { children: ReactNode }) {
  const client = useSyncExternalStore(authQueries.subscribe, authQueries.getSnapshot);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthQueryProvider>
      <BrowserRouter>
        <EntityColorPaletteProvider>
          <App />
        </EntityColorPaletteProvider>
      </BrowserRouter>
    </AuthQueryProvider>
  </StrictMode>,
);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' });
  });
}
